import React from 'react'

import { DateRange, getDefaultRange } from 'components/DateRangePicker'
import {
  ActivityType,
  activityTypeToString,
  ContactType,
  contactTypeToString
} from 'modules/accountActivity/domain'

export enum FilterValueType {
  STRING = 'STRING',
  NUMBER = 'NUMBER',
  DATE = 'DATE'
}

export enum FilterPlurality {
  SINGLE = 'SINGLE',
  MULTIPLE = 'MULTIPLE'
}

export enum FilterOperator {
  EQUALS = 'EQUALS',
  CONTAINS = 'CONTAINS',
  STARTS_WITH = 'STARTS_WITH',
  DOES_NOT_EQUAL = 'DOES_NOT_EQUAL',
  DOES_NOT_CONTAIN = 'DOES_NOT_CONTAIN',
  DOES_NOT_START_WITH = 'DOES_NOT_START_WITH',
  BETWEEN = 'BETWEEN',
  GREATER_THAN = 'GREATER_THAN',
  GREATER_THAN_EQ = 'GREATER_THAN_EQ',
  LESS_THAN = 'LESS_THAN',
  LESS_THAN_EQ = 'LESS_THAN_EQ'
}

export enum AccountType {
  ALL = 'ALL',
  OWNED = 'OWNED',
  TARGETED = 'TARGETED'
}

export enum FilterableEntity {
  ACTIVITIES = 'ACTIVITIES',
  CONTACT_TYPES = 'CONTACT_TYPES',
  ACCOUNT_OWNERS = 'ACCOUNT_OWNERS',
  DATE_RANGE = 'DATE_RANGE',
  PAGE_URL = 'PAGE_URL',
  TARGETED = 'TARGETED',
  CONTACT_ROLE = 'CONTACT_ROLE',
  NUMBER = 'NUMBER',
  CUSTOM_ATTRIBUTE = 'CUSTOM_ATTRIBUTE'
}

export interface FilterValues {
  filterType: FilterableEntity
  filterSubtype?: string
  attributeType?: string
  valueType?: string
  operator: FilterOperator
  values: any
}

export interface ContextFilter {
  field: string
  filterOperation: string
  attribute: string
  values: string[]
}

export interface ContextFilters {
  [key: string]: ContextFilter[]
}

export interface AttributeFilter {
  filterOperation: string
  attribute?: string
  values: string[]
}

export const ALL_ACTIVITY_TYPES = new Set([
  ActivityType.PAGE_VIEW,
  ActivityType.CONVERSATION,
  ActivityType.MEETING_BOOKED,
  ActivityType.VIDEO,
  ActivityType.EMAIL_OPEN,
  ActivityType.EMAIL_CLICK
])

export const NUMBER_OPERATOR_TYPES = new Set([
  FilterOperator.LESS_THAN_EQ,
  FilterOperator.LESS_THAN,
  FilterOperator.GREATER_THAN,
  FilterOperator.GREATER_THAN_EQ
])

export const ALL_CONTACT_TYPES = new Set([ContactType.KNOWN, ContactType.UNKNOWN])

export const operatorToString = (operator: FilterOperator): string => {
  switch (operator) {
    case FilterOperator.EQUALS:
      return 'is'
    case FilterOperator.CONTAINS:
      return 'contains'
    case FilterOperator.STARTS_WITH:
      return 'starts with'
    case FilterOperator.DOES_NOT_EQUAL:
      return 'is not'
    case FilterOperator.DOES_NOT_CONTAIN:
      return 'does not contain'
    case FilterOperator.DOES_NOT_START_WITH:
      return 'does not start with'
    case FilterOperator.BETWEEN:
      return 'between'
    case FilterOperator.GREATER_THAN:
      return '>'
    case FilterOperator.GREATER_THAN_EQ:
      return '≥'
    case FilterOperator.LESS_THAN:
      return '<'
    case FilterOperator.LESS_THAN_EQ:
      return '≤'
  }
}

export interface FilterConfiguration {
  filterType: FilterableEntity
  filterSubtype?: string
  filterDisplayName: string
  plurality: FilterPlurality
  operators: FilterOperator[]
  valueType: string
  extraWord: string
  creatable?: boolean
  gridTemplateStyle: { gridTemplateColumns: string }
  getIsFilterValid: (value: any) => boolean
  transformFilterForApi: (filter: any) => any
  attributeType?: string
}

export interface FilterConfigAndAvailableValues extends FilterConfiguration {
  values: any[]
}

export const filterConfig: FilterConfigAndAvailableValues[] = [
  {
    filterType: FilterableEntity.ACCOUNT_OWNERS,
    filterDisplayName: 'Account owner',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.EQUALS, FilterOperator.DOES_NOT_EQUAL],
    valueType: FilterValueType.STRING,
    values: [],
    extraWord: 'others',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return Array.isArray(value) && value.length > 0
    },
    transformFilterForApi: (filter: any) => {
      return filter && filter.values !== undefined && filter.values !== ''
        ? {
            field: 'acct.accountOwnerId',
            filterOperation: filter.operator,
            values: filter.values.map((value: any) => value.value)
          }
        : undefined
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
  },
  {
    filterType: FilterableEntity.ACTIVITIES,
    filterDisplayName: 'Activity type',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.EQUALS],
    valueType: FilterValueType.STRING,
    values: [...ALL_ACTIVITY_TYPES].map(activity => ({
      value: activity,
      label: activityTypeToString(activity)
    })),
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return Array.isArray(value) && value.length > 0
    },
    transformFilterForApi: (value: any) => {
      if (Array.isArray(value) && value.length > 0) {
        return value?.map((item: any) => item.value)
      }
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
  },
  {
    filterType: FilterableEntity.CONTACT_TYPES,
    filterDisplayName: 'Contact',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.EQUALS],
    valueType: FilterValueType.STRING,
    values: [...ALL_CONTACT_TYPES].map(contactType => ({
      value: contactType,
      label: contactTypeToString(contactType) as string
    })),
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return Array.isArray(value) && value.length > 0
    },
    transformFilterForApi: (value: any) => {
      if (Array.isArray(value) && value.length > 0)
        return getContactTypes(new Set(value?.map((item: any) => item.value)))
      return getContactTypes(ALL_CONTACT_TYPES)
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
  },
  {
    filterType: FilterableEntity.TARGETED,
    filterDisplayName: 'Account type',
    plurality: FilterPlurality.SINGLE,
    operators: [FilterOperator.EQUALS],
    valueType: FilterValueType.STRING,
    values: [
      { label: 'Targeted', value: AccountType.TARGETED },
      { label: 'All', value: AccountType.ALL }
    ],
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return Object.values(AccountType).includes(value?.value)
    },
    transformFilterForApi: (value: any) => {
      return value?.value === AccountType.TARGETED ? true : false
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
  },
  {
    filterType: FilterableEntity.PAGE_URL,
    filterDisplayName: 'Page visited',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.CONTAINS, FilterOperator.DOES_NOT_CONTAIN],
    valueType: FilterValueType.STRING,
    values: [],
    extraWord: 'more',
    creatable: true,
    getIsFilterValid: (value: any) => {
      return Array.isArray(value) && value.length > 0
    },
    transformFilterForApi: (filter: any) => {
      return filter && filter.values !== undefined && filter.values !== ''
        ? {
            PAGE_VIEW: [
              {
                field: 'act.activityContext',
                filterOperation: filter.operator,
                attribute: 'pageUrl',
                values: filter.values.map((value: any) => value.value)
              }
            ]
          }
        : undefined
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 100px auto 32px' }
  },
  {
    filterType: FilterableEntity.CONTACT_ROLE,
    filterDisplayName: 'Contact Role',
    plurality: FilterPlurality.MULTIPLE,
    operators: [FilterOperator.CONTAINS, FilterOperator.DOES_NOT_CONTAIN],
    valueType: FilterValueType.STRING,
    values: [],
    extraWord: 'more',
    creatable: true,
    getIsFilterValid: (value: any) => {
      return Array.isArray(value) && value.length > 0
    },
    transformFilterForApi: (filter: any) => {
      return filter && filter.values !== undefined && filter.values !== ''
        ? {
            field: 'act.endUserData',
            filterOperation: filter.operator,
            attribute: 'role',
            values: filter.values.map((value: any) => value.value)
          }
        : undefined
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 100px auto 32px' }
  },
  {
    filterType: FilterableEntity.NUMBER,
    filterDisplayName: 'Number',
    plurality: FilterPlurality.SINGLE,
    operators: [
      FilterOperator.EQUALS,
      FilterOperator.DOES_NOT_EQUAL,
      FilterOperator.LESS_THAN,
      FilterOperator.LESS_THAN_EQ,
      FilterOperator.GREATER_THAN,
      FilterOperator.GREATER_THAN_EQ
    ],
    valueType: FilterValueType.NUMBER,
    values: [],
    extraWord: 'more',
    creatable: false,
    getIsFilterValid: (value: any) => {
      return typeof value === 'number'
    },
    transformFilterForApi: (filter: FilterValues) => {
      return typeof filter.values === 'number' ? [filter.values] : undefined
    },
    gridTemplateStyle: { gridTemplateColumns: '170px 100px auto 32px' }
  }
]

export const filterMap = filterConfig.reduce(
  (acc: { [key: string]: FilterConfiguration }, filterConfig: FilterConfiguration) => {
    acc[filterConfig.filterType] = filterConfig
    return acc
  },
  {}
)

export interface AttributeOption {
  label: string
  type: string
  options?: string[]
}

export const attributeOptionToFilter = (
  attribute: AttributeOption
): FilterConfigAndAvailableValues => {
  const filterSubtype = attribute.label
  const filterDisplayName = attribute.label
  const values = attribute.options
    ? attribute.options.map((option: any) => ({ label: option, value: option }))
    : []
  const attributeType = attribute.type
  const creatable =
    (!attribute.options || attribute.options.length === 0) && attributeType !== 'TEAMMEMBER'

  const templateFilter =
    attributeType === 'DATE' || attributeType === 'DATETIME'
      ? filterMap[FilterableEntity.DATE_RANGE]
      : attributeType === 'NUMBER'
      ? filterMap[FilterableEntity.NUMBER]
      : {
          plurality: FilterPlurality.MULTIPLE,
          operators: [FilterOperator.EQUALS, FilterOperator.DOES_NOT_EQUAL],
          valueType: FilterValueType.STRING,
          extraWord: 'more',
          getIsFilterValid: (value: any) => {
            return Array.isArray(value) && value.length > 0
          },
          transformFilterForApi: (x: any) => x,
          gridTemplateStyle: { gridTemplateColumns: '170px 75px auto 32px' }
        }

  return {
    ...templateFilter,
    filterType: FilterableEntity.CUSTOM_ATTRIBUTE,
    filterSubtype,
    filterDisplayName,
    values,
    creatable,
    attributeType
  }
}

export const getContactTypes = (contactTypes: Set<ContactType>) => {
  if (contactTypes.size === 0) return ContactType.ALL
  if (contactTypes.size === 2) return ContactType.ALL
  if (contactTypes.has(ContactType.KNOWN)) return ContactType.KNOWN
  return ContactType.UNKNOWN
}

export type FilterGroup = FilterValues[]

export const MAX_NUM_FILTER_GROUPS = 6

export const initialGroupsByPage = {
  ACCOUNTS: [[]],
  ACTIVITIES: [[]],
  ACCOUNT_DETAILS: [[]],
  CONTACT_DETAILS: [[]]
}

export enum FilterPage {
  ACCOUNTS = 'ACCOUNTS',
  ACTIVITIES = 'ACTIVITIES',
  ACCOUNT_DETAILS = 'ACCOUNT_DETAILS',
  CONTACT_DETAILS = 'CONTACT_DETAILS'
}

export interface FilterGroupsByPage {
  ACCOUNTS: FilterGroup[]
  ACTIVITIES: FilterGroup[]
  ACCOUNT_DETAILS: FilterGroup[]
  CONTACT_DETAILS: FilterGroup[]
}

export interface FilterContextType {
  searchQuery: string
  setSearchQuery: React.Dispatch<React.SetStateAction<string>>
  dateRange: DateRange | undefined
  setDateRange: React.Dispatch<React.SetStateAction<DateRange | undefined>>
  filterGroupsByPage: FilterGroupsByPage
  onRemoveFilter: (page: FilterPage, groupIndex: number, filterIndex: number) => void
  onChangeFilterGroups: (filterGroups: FilterGroup[], page: FilterPage) => void
  onClearFilterGroups: (page: FilterPage) => void
}

export const FiltersContext = React.createContext<FilterContextType>({
  searchQuery: '',
  setSearchQuery: () => {},
  dateRange: getDefaultRange(),
  setDateRange: () => {},
  filterGroupsByPage: initialGroupsByPage,
  onRemoveFilter: () => {},
  onChangeFilterGroups: () => {},
  onClearFilterGroups: () => {}
})
